import { useEffect } from 'react';

/**
 * Lightweight per-page SEO for the public pages — sets document.title,
 * meta description, og:title/og:description, and a canonical link, and
 * restores the site-wide defaults (from public/index.html) on unmount.
 *
 * Deliberately not react-helmet: only a handful of public routes need this,
 * and Google's crawler executes JS so runtime tags are what it indexes.
 * Link-preview bots (WhatsApp/Facebook) don't run JS — those go through the
 * server-rendered /share/* routes instead.
 *
 * Usage: <Seo title="Verified Contractors in Zimbabwe" description="…" canonicalPath="/contractors" />
 * Renders nothing.
 */

const BASE_URL = 'https://www.eqaya.com';

const DEFAULT_TITLE = 'Eqaya — Trusted Contractors & Construction Projects in Zimbabwe';
const DEFAULT_DESCRIPTION = 'Eqaya is a trust platform for diasporans building, renovating and repairing property in Zimbabwe while away. Verified contractors do the work. Independent verifiers inspect it, with GPS proof and photos. You release payment when satisfied.';

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const applyTags = (title, description, canonicalPath) => {
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  let link = document.head.querySelector('link[rel="canonical"]');
  if (canonicalPath) {
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', `${BASE_URL}${canonicalPath}`);
  } else if (link) {
    link.remove();
  }
};

const Seo = ({ title, description, canonicalPath }) => {
  useEffect(() => {
    const fullTitle = title ? `${title} | Eqaya` : DEFAULT_TITLE;
    applyTags(fullTitle, description || DEFAULT_DESCRIPTION, canonicalPath || null);
    return () => applyTags(DEFAULT_TITLE, DEFAULT_DESCRIPTION, null);
  }, [title, description, canonicalPath]);

  return null;
};

export default Seo;
